import React, { useState } from 'react';
import { Plus, Edit, Trash2, Clock, Eye, X, Phone, Mail, MapPin, Calendar, Shield } from 'lucide-react';
import { useCompanies } from '../../hooks/useCompanies';
import { activateTrialForCompany } from '../../services/authService';
import { formatters } from '../../utils/formatters';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import CompanyForm from './CompanyForm';

const CompaniesManager = () => {
  const { companies, loading, addCompany, updateCompany, deleteCompany } = useCompanies();
  const [showForm, setShowForm] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [viewingCompany, setViewingCompany] = useState(null);
  const [activatingTrial, setActivatingTrial] = useState(null);

  const handleEdit = (company) => {
    setEditingCompany(company);
    setShowForm(true);
  };

  const handleViewDetails = (company) => {
    setViewingCompany(company);
    setShowDetailsModal(true);
  };

  const handleEditFromDetails = () => {
    setShowDetailsModal(false);
    setEditingCompany(viewingCompany);
    setShowForm(true);
  };

  const handleDelete = async (companyId) => {
    if (window.confirm('Tem certeza que deseja excluir esta empresa?')) {
      try {
        await deleteCompany(companyId);
      } catch (error) {
        alert('Erro ao excluir empresa: ' + error.message);
      }
    }
  };

  const handleActivateTrial = async (company) => {
    const confirmed = window.confirm(
      `Ativar período de teste de 5 dias para a empresa "${company.name}"?\n\nIsso sobrescreverá qualquer período de teste anterior.`
    );
    
    if (!confirmed) return;
    
    setActivatingTrial(company.id);
    
    try {
      await activateTrialForCompany(company.id, 5);
      alert('Período de teste ativado com sucesso!');
      // Atualizar a lista de empresas
      window.location.reload();
    } catch (error) {
      alert('Erro ao ativar período de teste: ' + error.message);
    } finally {
      setActivatingTrial(null);
    }
  };

  const handleToggleRentalAccess = async (company) => {
    const newAccess = !company.rentalAccess;
    const action = newAccess ? 'habilitar' : 'desabilitar';
    
    const confirmed = window.confirm(
      `${action.charAt(0).toUpperCase() + action.slice(1)} o módulo de aluguéis para a empresa "${company.name}"?`
    );
    
    if (!confirmed) return;
    
    try {
      const { companyService } = await import('../../services/companyService');
      await companyService.updateRentalAccess(company.id, newAccess);
      
      // Atualizar a lista de empresas
      window.location.reload();
      
      alert(`Módulo de aluguéis ${newAccess ? 'habilitado' : 'desabilitado'} com sucesso!`);
    } catch (error) {
      alert('Erro ao atualizar acesso aos aluguéis: ' + error.message);
    }
  };

  const handleSubmit = async (companyData) => {
    try {
      if (editingCompany) {
        await updateCompany(editingCompany.id, companyData);
      } else {
        await addCompany(companyData);
      }
      setShowForm(false);
      setEditingCompany(null);
    } catch (error) {
      alert('Erro ao salvar empresa: ' + error.message);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCompany(null);
  };

  // Componente para exibir detalhes da empresa
  const CompanyDetailsModal = ({ company, isOpen, onClose, onEdit }) => {
    if (!company) return null;

    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Detalhes da Empresa</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6 max-h-96 overflow-y-auto">
            {/* Informações Básicas */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                Informações Básicas
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Nome da Empresa</label>
                  <p className="text-lg font-semibold text-gray-900">{company.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                  <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                    company.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {formatters.formatStatus(company.active)}
                  </span>
                </div>
              </div>
            </div>

            {/* Informações de Contato */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Mail className="w-5 h-5 mr-2" />
                Contato
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                  <p className="text-gray-900 flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-gray-500" />
                    <a href={`mailto:${company.email}`} className="text-blue-600 hover:text-blue-800">
                      {company.email}
                    </a>
                  </p>
                </div>
                {company.phone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Telefone</label>
                    <p className="text-gray-900 flex items-center">
                      <Phone className="w-4 h-4 mr-2 text-gray-500" />
                      <a href={`tel:${company.phone}`} className="text-blue-600 hover:text-blue-800">
                        {formatters.formatPhone(company.phone)}
                      </a>
                      {company.isWhatsApp && (
                        <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                          WhatsApp
                        </span>
                      )}
                    </p>
                  </div>
                )}
                {company.address && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Endereço</label>
                    <p className="text-gray-900 flex items-start">
                      <MapPin className="w-4 h-4 mr-2 mt-1 text-gray-500 flex-shrink-0" />
                      <span className="whitespace-pre-line">{formatters.formatAddress(company.address)}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Informações do Sistema */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Informações do Sistema
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Data de Criação</label>
                  <p className="text-gray-900">{formatters.formatDateTime(company.createdAt)}</p>
                </div>
                {company.updatedAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Última Atualização</label>
                    <p className="text-gray-900">{formatters.formatDateTime(company.updatedAt)}</p>
                  </div>
                )}
                {company.lastAccess ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Último Acesso</label>
                    <p className="text-gray-900">{formatters.formatDateTime(company.lastAccess)}</p>
                    {company.lastAccessBy && (
                      <p className="text-sm text-gray-500 mt-1">por: {company.lastAccessBy}</p>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Último Acesso</label>
                    <p className="text-gray-500">Nunca acessou o sistema</p>
                  </div>
                )}
              </div>
            </div>

            {/* Período de Teste */}
            {company.isTrial && (
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  Período de Teste
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-600 mb-1">Status</label>
                    <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                      Trial Ativo
                    </span>
                  </div>
                  {company.trialEndDate && (
                    <div>
                      <label className="block text-sm font-medium text-blue-600 mb-1">Data de Término</label>
                      <p className="text-blue-900">{formatters.formatDate(company.trialEndDate)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Fechar
            </button>
            <button
              onClick={onEdit}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Edit className="w-4 h-4" />
              <span>Editar</span>
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="py-20" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Gerenciar Empresas</h2>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Nova Empresa</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telefone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trial
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aluguéis
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Criação
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Último Acesso
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {companies.map(company => (
                <tr key={company.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleViewDetails(company)}
                      className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer transition-colors"
                    >
                      {company.name}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {company.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    <div>
                      {company.phone ? formatters.formatPhone(company.phone) : '-'}
                      {company.phone && company.isWhatsApp && (
                        <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                          WhatsApp
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      company.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {formatters.formatStatus(company.active)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {company.isTrial ? (
                      <div className="text-xs">
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                          Trial Ativo
                        </span>
                        {company.trialEndDate && (
                          <div className="text-gray-500 mt-1">
                            Até: {formatters.formatDate(company.trialEndDate)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">Não</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleRentalAccess(company)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        company.rentalAccess 
                          ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {company.rentalAccess ? 'Habilitado' : 'Desabilitado'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {formatters.formatDate(company.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {company.lastAccess ? (
                      <div className="text-xs">
                        <div>{formatters.formatDateTime(company.lastAccess)}</div>
                        {company.lastAccessBy && (
                          <div className="text-gray-500 mt-1">
                            por: {company.lastAccessBy}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">Nunca acessou</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewDetails(company)}
                        className="text-gray-600 hover:text-gray-800"
                        title="Visualizar detalhes"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(company)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Editar empresa"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleActivateTrial(company)}
                        disabled={activatingTrial === company.id}
                        className="text-green-600 hover:text-green-800 disabled:opacity-50"
                        title="Ativar período de teste"
                      >
                        {activatingTrial === company.id ? (
                          <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Clock className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(company.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Excluir empresa"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {companies.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nenhuma empresa cadastrada ainda
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showForm}
        onClose={handleCloseForm}
        title={editingCompany ? 'Editar Empresa' : 'Nova Empresa'}
      >
        <CompanyForm
          company={editingCompany}
          onSubmit={handleSubmit}
          onCancel={handleCloseForm}
        />
      </Modal>

      <CompanyDetailsModal
        company={viewingCompany}
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setViewingCompany(null);
        }}
        onEdit={handleEditFromDetails}
      />
    </div>
  );
};

export default CompaniesManager;